import { DAMAGE_MILLI_PER_UNIT } from './base';
import { BPS_ONE } from './exact';

/**
 * **A aritmética do Roubo de Vida, e a fronteira onde ela encontra uma barra.**
 *
 * ## O que esta joia é, e o que ela deliberadamente não é
 *
 * Não é um proc. Não sorteia, não consome entropia, não ocupa intervalo na
 * loteria do disparo e não pode ser escolhida em vez do Ciclone. Ativa em
 * **todos** os golpes elegíveis e devolve uma percentagem do dano que foi
 * **realmente aplicado** - ADR 0054, que revoga a D-4.3 do V28D.2 e a entrada
 * dela na loteria do V28G.F.
 *
 * A consequência prática é que o overkill não cura: um tiro de 40 pontos num
 * casco com 3 de vida aplica 3, e a cura sai de 3.
 *
 * ## Porque este ficheiro não conhece nenhuma joia
 *
 * Recebe `lifestealBps`, um coeficiente que `resolveJewelEffects` derivou do
 * perfil congelado. Não sabe o que é `levelSum`, não sabe o que é uma cópia, não
 * lê o catálogo e não tem o id de joia nenhuma escrito em lado nenhum - o que é
 * uma regra do projeto e não um estilo: `jewel-effects-absent.test.ts` varre
 * todos os módulos de produto e falha se algum deles nomear uma das quinze. É o
 * que permite às duas simulações aplicarem cura sem um `if` por joia, e é a
 * mesma disciplina que `damage/exact.ts` já tem para o dano.
 *
 * ## Porque `bigint` até ao fim
 *
 * Porque `appliedDamageMilli` deriva de um dano que não tem tecto de jogo - cada
 * Coração Primordial incorporado soma 100 basis points e a contagem vive em
 * `numeric(40, 0)`. Multiplicar isso por um coeficiente e converter antes de
 * comparar seria capar um item num tipo, que é o que o `CLAUDE.md` proíbe
 * absolutamente. Aqui compara-se primeiro; converte-se só o ramo que a
 * comparação já provou pequeno.
 *
 * ## As duas fronteiras, e porque são duas
 *
 * Pela mesma razão que `resolveStrike` e `resolveIntegerStrike` são duas: a vida
 * de um mob é um `number` fracionário e a de um casco de duelo é decodificada
 * como INTEIRO por `snapshot-v3`. Uma só função teria de escolher uma das duas
 * representações e mentir sobre a outra.
 */

/**
 * Um milésimo de ponto de vida.
 *
 * É `DAMAGE_MILLI_PER_UNIT` e não uma terceira constante igual, e isso é uma
 * afirmação em vez de uma conveniência: a cura sai do dano aplicado multiplicado
 * por um coeficiente ADIMENSIONAL, portanto ela nasce na escala do dano. Nas
 * duas simulações um ponto de dano remove um ponto de vida, logo as duas escalas
 * são a mesma por construção. Um segundo `1000` aqui seria um sítio onde as duas
 * podiam divergir sem que nada o assinalasse.
 */
export const HEAL_MILLI_PER_UNIT = DAMAGE_MILLI_PER_UNIT;

const MILLI = BigInt(HEAL_MILLI_PER_UNIT);

/**
 * O módulo do resto sub-milli: `[0, 10 000)`.
 *
 * A percentagem produz uma fração de um MILÉSIMO de ponto de vida, e é essa
 * fração que este resto guarda. O tecto do que ele pode representar é um milli,
 * ou seja um milésimo de ponto - motivo pelo qual ele nunca pode virar uma
 * reserva de cura percetível, por muito tempo que acumule.
 */
export const LIFESTEAL_CARRY_MODULUS = Number(BPS_ONE);

/** O módulo do resto de PONTO INTEIRO: `[0, 1 000)`. Só o duelo precisa dele. */
export const HEAL_CARRY_MODULUS = HEAL_MILLI_PER_UNIT;

/**
 * O que a percentagem produziu, e o que sobrou dela.
 *
 * `numerador = danoAplicadoMilli x lifestealBps + resto`, uma divisão no fim, e
 * o resto guardado. Sem ele, mil golpes de um milli a 9 % curariam **zero** em
 * vez de 90 milli: cada um deles vale 0,009 milli, e nove milésimos truncados
 * mil vezes são nove milésimos perdidos mil vezes.
 *
 * É a mesma resposta que o projeto dá em todo o lado onde uma fração encontra um
 * inteiro - `damageCarryMilli`, `regenCarryMilli`, o resíduo sub-milissegundo do
 * `HuntClock` e o da cadência do duelo em milli-tiques.
 */
export interface LifestealEarned {
  /** A cura que a percentagem produziu, em milli. Ainda sem limite nenhum. */
  readonly healMilli: bigint;
  /** O resto sub-milli para o golpe SEGUINTE, sempre em `[0, 10 000)`. */
  readonly nextCarryBps: number;
}

/** Nada aconteceu: nenhuma cura, nenhum limite, e todos os restos intactos. */
const NOTHING_HAPPENED = (carryBps: number): LifestealResolution =>
  Object.freeze({
    potentialMilli: 0n,
    applied: 0,
    nextCarryBps: carryBps,
    clamped: false,
    converted: false,
  });

/**
 * A percentagem, exata, e mais nada.
 *
 * Pura no sentido forte: sem alvo, sem atacante, sem vida, sem limite e sem
 * conhecimento nenhum do que é uma barra. Quem a limita é uma das duas
 * fronteiras abaixo.
 *
 * @param appliedDamageMilli o dano que foi REALMENTE removido do alvo, já depois
 *   do clamp do overkill. Nunca o dano do tiro, nunca o dano-base e nunca a
 *   intenção.
 * @param lifestealBps o coeficiente que `resolveJewelEffects` derivou do perfil
 *   congelado, já com o teto da joia aplicado.
 * @param carryBps o resto sub-milli que este atacante trazia, em `[0, 10 000)`.
 */
export function lifestealEarnedMilli(
  appliedDamageMilli: bigint,
  lifestealBps: bigint,
  carryBps: number,
): LifestealEarned {
  if (!Number.isSafeInteger(carryBps) || carryBps < 0 || carryBps >= LIFESTEAL_CARRY_MODULUS) {
    throw new RangeError(
      `a lifesteal carry is in [0, ${LIFESTEAL_CARRY_MODULUS}), got ${carryBps}`,
    );
  }
  if (lifestealBps < 0n) {
    throw new RangeError(`a lifesteal coefficient is never negative, got ${lifestealBps}`);
  }
  if (appliedDamageMilli < 0n) {
    throw new RangeError(`applied damage is never negative, got ${appliedDamageMilli}`);
  }
  if (lifestealBps === 0n) {
    /*
     * Sem joia, o resto NÃO se mexe - e é essa a diferença entre "não curou" e
     * "curou zero". Um casco sem Orbe Vampírico tem de produzir exatamente o
     * mesmo estado que produzia antes deste gate existir, byte a byte, e um
     * resto que fosse reescrito com o mesmo valor continuaria a ser um campo
     * que a instantânea teria de justificar.
     */
    return Object.freeze({ healMilli: 0n, nextCarryBps: carryBps });
  }
  if (appliedDamageMilli === 0n) {
    // Um golpe que não removeu nada não é um golpe. O resto fica onde estava.
    return Object.freeze({ healMilli: 0n, nextCarryBps: carryBps });
  }
  const numerator = appliedDamageMilli * lifestealBps + BigInt(carryBps);
  return Object.freeze({
    healMilli: numerator / BPS_ONE,
    /*
     * O resto cabe sempre num `number`: é estritamente menor que `BPS_ONE`, por
     * definição de resto. Esta é a única conversão desta função, e é a única que
     * é segura sem uma comparação a provar-lhe o tamanho.
     */
    nextCarryBps: Number(numerator % BPS_ONE),
  });
}

/**
 * O que uma cura fez a uma barra, dita por inteiro.
 *
 * Quatro respostas e não uma, porque um chamador tem quatro perguntas
 * diferentes: quanto é que a joia valia, quanto é que a barra aceitou, o que
 * fica para o golpe seguinte, e se o limite chegou a morder.
 */
export interface LifestealResolution {
  /** A cura TEÓRICA, em milli: o que a percentagem produziu, antes do limite. */
  readonly potentialMilli: bigint;
  /** A cura que a barra REALMENTE recebeu, na unidade do modo. Nunca negativa. */
  readonly applied: number;
  /** O resto sub-milli para o golpe seguinte. */
  readonly nextCarryBps: number;
  /** Verdadeiro quando a vida em falta - ou a morte - limitou a cura. */
  readonly clamped: boolean;
  /**
   * Se algum `bigint` teve de ser convertido para chegar aqui.
   *
   * Falso sempre que o limite mordeu, porque nesse ramo a resposta é a vida em
   * falta e o valor grande nunca precisa de existir como `double`. É afirmado
   * pelos testes e não apenas documentado, pela mesma razão que
   * {@link import('./exact').StrikeResolution.converted} o é.
   */
  readonly converted: boolean;
}

/** A mesma resposta para uma barra INTEIRA, com o resto que ela obriga a ter. */
export interface IntegerLifestealResolution extends LifestealResolution {
  /** Milli-vida ganha mas ainda não entregue, em `[0, 1 000)`. */
  readonly nextCarryMilli: number;
}

/**
 * **A fronteira, para uma simulação cuja vida é um `number` fracionário.**
 *
 * A caça. `maxHealth()` é a base vezes uma percentagem somada, portanto a vida
 * de uma nave é fracionária por construção e absorve a fração de uma cura
 * diretamente - não há segundo resto a guardar.
 *
 * ## O limite é uma comparação, nunca uma conversão
 *
 * A vida em falta é escalada PARA CIMA em milli, dando um tecto exato que um
 * `double` sabe sempre exprimir. Uma cura maior do que ele entrega exatamente a
 * vida em falta e **nada é convertido**; uma cura menor está provada pequena
 * antes de o ser. Uma cura de `10^38` milli e uma de `10^9` enchem uma barra de
 * 40 da mesma maneira.
 *
 * ## Nunca
 *
 * Overheal, escudo, ressurreição, `Infinity`, `NaN`, saturação ou um
 * coeficiente congelado. Não há limite nenhum sobre a CURA nesta função - há um
 * limite sobre o que uma barra pode receber, que é uma afirmação diferente e uma
 * afirmação verdadeira.
 */
export function resolveLifestealHeal(input: {
  readonly appliedDamageMilli: bigint;
  readonly lifestealBps: bigint;
  readonly carryBps: number;
  readonly currentHealth: number;
  readonly maxHealth: number;
}): LifestealResolution {
  const { currentHealth, maxHealth } = input;
  if (!Number.isFinite(currentHealth) || !Number.isFinite(maxHealth)) {
    throw new RangeError(`health must be finite, got ${currentHealth}/${maxHealth}`);
  }
  if (input.lifestealBps <= 0n) {
    /*
     * Sem coeficiente não houve acontecimento nenhum, e é por isso que isto
     * está ANTES de tudo o resto - incluindo antes da pergunta sobre a morte.
     *
     * Uma nave sem o Orbe Vampírico tem de sair daqui com exatamente o estado
     * com que entrou, em todos os campos e em todos os ramos. Fazê-lo
     * estruturalmente, com um retorno, é diferente de o conseguir por os outros
     * ramos calharem devolver zero: o segundo é verdade hoje e é uma coisa que
     * um ramo novo pode partir sem que nada o assinale.
     */
    return NOTHING_HAPPENED(input.carryBps);
  }
  if (currentHealth <= 0) {
    /*
     * Um destroço não recebe cura, e não guarda resto nenhum.
     *
     * Não é uma otimização: é a proibição de ressuscitar, escrita no único sítio
     * onde ela pode ser esquecida. Um chamador que se lembrasse dela seria um
     * chamador; esta é a função.
     */
    return Object.freeze({
      potentialMilli: 0n,
      applied: 0,
      nextCarryBps: input.carryBps,
      clamped: true,
      converted: false,
    });
  }
  const earned = lifestealEarnedMilli(input.appliedDamageMilli, input.lifestealBps, input.carryBps);
  const missing = maxHealth - currentHealth;
  if (missing <= 0 || earned.healMilli <= 0n) {
    /*
     * Vida cheia: a cura aplicada é ZERO e a passiva continua ativa. O resto
     * sub-milli é largado em vez de guardado, pela mesma regra que `tickRegen`
     * já segue - uma longa estadia com a vida cheia não pode virar uma rajada de
     * cura mais tarde.
     */
    return Object.freeze({
      potentialMilli: earned.healMilli,
      applied: 0,
      nextCarryBps: missing <= 0 ? 0 : earned.nextCarryBps,
      clamped: missing <= 0,
      converted: false,
    });
  }
  const ceilingMilli = BigInt(Math.ceil(missing * HEAL_MILLI_PER_UNIT));
  if (earned.healMilli >= ceilingMilli) {
    // Enche a barra. Quanto maior era não é uma pergunta que alguém precise de
    // ver respondida: o excedente desaparece, e é isso o "sem overheal".
    return Object.freeze({
      potentialMilli: earned.healMilli,
      applied: missing,
      nextCarryBps: 0,
      clamped: true,
      converted: false,
    });
  }
  /*
   * Provado pequeno: estritamente abaixo de um tecto que já é um inteiro
   * seguro. Esta conversão não pode perder um dígito, e é a única aqui.
   */
  return Object.freeze({
    potentialMilli: earned.healMilli,
    applied: Number(earned.healMilli) / HEAL_MILLI_PER_UNIT,
    nextCarryBps: earned.nextCarryBps,
    clamped: false,
    converted: true,
  });
}

/**
 * **A mesma fronteira, para uma simulação cuja vida é um número inteiro.**
 *
 * O duelo. `snapshot-v3` decodifica `health` com um leitor de INTEIROS, portanto
 * um casco em `359,6` é um estado que o formato não sabe escrever - e um formato
 * que não sabe escrever um estado é um formato que perde a partida na
 * reconexão seguinte.
 *
 * Truncar cada cura em vez disso seria pior, e é exatamente o que o `CLAUDE.md`
 * proíbe: ao `levelSum` 30 um tiro de 40 devolve 3,6 pontos, e um casco que
 * recebesse 3 perderia dezassete por cento do que a joia vale, a cada tiro.
 *
 * ## Por isso o resto é GUARDADO, no casco que o ganhou
 *
 * O espelho exato de `damageCarryMilli`, que já vive ao lado dele por escrito e
 * pela mesma razão. Três curas de 3,6 devolvem 3, 4 e 3 - dez pontos, que é
 * `10,8` truncado uma vez no fim em vez de três vezes pelo caminho.
 *
 * O resto vive no ATACANTE, e não no par nem no alvo: é cura devida a ESTE
 * casco, e um destroço não a leva para a ronda seguinte.
 *
 * @param currentHealth pontos inteiros. Uma vida fracionária é recusada em vez
 *   de arredondada: significaria que o casco do chamador já carrega uma fração
 *   que esta função existe para manter fora dele.
 */
export function resolveIntegerLifestealHeal(input: {
  readonly appliedDamageMilli: bigint;
  readonly lifestealBps: bigint;
  readonly carryBps: number;
  readonly carryMilli: number;
  readonly currentHealth: number;
  readonly maxHealth: number;
}): IntegerLifestealResolution {
  const { currentHealth, maxHealth, carryMilli } = input;
  if (!Number.isSafeInteger(currentHealth) || !Number.isSafeInteger(maxHealth)) {
    throw new RangeError(`health must be a whole number, got ${currentHealth}/${maxHealth}`);
  }
  if (!Number.isSafeInteger(carryMilli) || carryMilli < 0 || carryMilli >= HEAL_CARRY_MODULUS) {
    throw new RangeError(`a heal carry is in [0, ${HEAL_CARRY_MODULUS}), got ${carryMilli}`);
  }
  if (input.lifestealBps <= 0n) {
    // Sem coeficiente não houve acontecimento nenhum. Ver a nota gémea acima.
    return Object.freeze({ ...NOTHING_HAPPENED(input.carryBps), nextCarryMilli: carryMilli });
  }
  if (currentHealth <= 0) {
    // Um destroço não recebe cura e não ressuscita. Ver a nota gémea acima.
    return Object.freeze({
      potentialMilli: 0n,
      applied: 0,
      nextCarryBps: input.carryBps,
      nextCarryMilli: carryMilli,
      clamped: true,
      converted: false,
    });
  }
  const earned = lifestealEarnedMilli(input.appliedDamageMilli, input.lifestealBps, input.carryBps);
  const missing = maxHealth - currentHealth;
  if (missing <= 0) {
    /*
     * Vida cheia. Os dois restos são largados: o de ponto inteiro chega a valer
     * 0,999 de vida, e guardá-lo faria uma estadia com a vida cheia virar um
     * ponto extra no primeiro golpe depois de levar dano - que é exatamente a
     * rajada que `tickRegen` já se recusa a produzir.
     */
    return Object.freeze({
      potentialMilli: earned.healMilli,
      applied: 0,
      nextCarryBps: 0,
      nextCarryMilli: 0,
      clamped: true,
      converted: false,
    });
  }
  const total = earned.healMilli + BigInt(carryMilli);
  if (total <= 0n) {
    return Object.freeze({
      potentialMilli: earned.healMilli,
      applied: 0,
      nextCarryBps: earned.nextCarryBps,
      nextCarryMilli: carryMilli,
      clamped: false,
      converted: false,
    });
  }
  const ceilingMilli = BigInt(missing) * MILLI;
  if (total >= ceilingMilli) {
    return Object.freeze({
      potentialMilli: earned.healMilli,
      applied: missing,
      nextCarryBps: 0,
      nextCarryMilli: 0,
      clamped: true,
      converted: false,
    });
  }
  /*
   * Provado pequeno: estritamente abaixo de `missing x 1000`, e `missing` é um
   * inteiro seguro. Nem esta divisão nem este resto podem perder um dígito.
   */
  return Object.freeze({
    potentialMilli: earned.healMilli,
    applied: Number(total / MILLI),
    nextCarryBps: earned.nextCarryBps,
    nextCarryMilli: Number(total % MILLI),
    clamped: false,
    converted: true,
  });
}
